/**
 * useEventsByPerformance — React Query hook for fetching the
 * `performance_events` join rows for a given performance.
 *
 * Why `performance_events` rather than `events`:
 *   - A performance can include multiple events (rodeo → performances → events).
 *   - The normalized relationship lives in the `performance_events` join table,
 *     where rows carry both `performance_id` and `event_id`.
 *   - Consuming callers that need the full `events` row can compose
 *     `useEventsByIds(eventIds)` on top of this, but we intentionally avoid
 *     premature coupling here (YAGNI).
 *
 * Important notes:
 *   - Uses the FLAT filter syntax: `?performance_id=<id>`.
 *   - The generated client already unwraps `{ data: [...] }` to a plain array.
 *   - The query key avoids including `undefined` — when `performanceId` is
 *     absent we fall back to the generic `lists()` key so cache identity
 *     remains consistent and broad invalidations still match.
 */

import { useQuery } from '@tanstack/react-query';
import { PerformanceEventsClient } from '../api/generated_clients/generated-performance_events-api-client';
import { performanceEventsKeys } from '../api/generated_hooks/generated-performance_events-hooks';
import type { PerformanceEventsRow } from '../types/generated_schemas/generated-performance_events-schema';

/**
 * Fetch all performance_events (join rows) for a given performance.
 *
 * @param performanceId - The parent performance id. When undefined/empty the
 *                        query is disabled.
 * @returns React Query result with an array of `PerformanceEventsRow`.
 */
export function useEventsByPerformance(performanceId: string | undefined) {
  const client = new PerformanceEventsClient();

  return useQuery<PerformanceEventsRow[]>({
    queryKey: performanceId
      ? performanceEventsKeys.list({ performance_id: performanceId })
      : performanceEventsKeys.lists(),
    queryFn: () =>
      client.getAll({
        // FLAT filter — generated client serializes to `?performance_id=<id>`.
        filters: { performance_id: performanceId! },
      }),
    enabled: !!performanceId,
  });
}
